import { Component, DestroyRef, ElementRef, OnInit, computed, inject, input, signal, viewChild, output} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RestService } from 'src/app/services/rest/rest.service';
import { NavController, ModalController, PopoverController } from '@ionic/angular';
import { RecentSearchesService } from 'src/app/services/recent-searches/recent-searches.service';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UiFeedbackService } from '../../services/ui-feedback/ui-feedback.service';

import { ENDPOINTS, Endpoint, SUGERENCIAS_VACIAS, Sugerencia } from './sugerencias';

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    // Era un @HostListener (§4 paso 7 del plan).
    host: {
        '(document:click)': 'click($event)',
    },
    standalone: false
})
export class SearchComponent implements OnInit {
  private ui = inject(UiFeedbackService);
  private rest = inject(RestService);
  private nav = inject(NavController);
  private route = inject(ActivatedRoute);
  private recientes = inject(RecentSearchesService);
  private analytics = inject(AnalyticsService);
  private modal = inject(ModalController);
  private popover = inject(PopoverController);
  private destroyRef = inject(DestroyRef);

  readonly mobile = input(false);

  /** El término escrito. Señal: lo lee la plantilla y también `app-top-searches`. */
  readonly searchTerm = signal<string>('');

  readonly searchOpened = signal(false);
  readonly loading = signal(false);
  readonly topSearch = signal<any[]>([]);

  /**
   * Señal, y se reemplaza entera en cada cambio: antes era un objeto mutado desde los
   * callbacks del servicio. Sin cambiar de identidad no repinta ni esta plantilla ni
   * `app-top-searches`, que lo recibe como entrada.
   */
  readonly suggestions = signal<Record<Endpoint, Sugerencia>>(SUGERENCIAS_VACIAS());

  /** La lupa se cambia por el spinner mientras haya alguna de las cuatro en marcha. */
  readonly buscando = computed(() => ENDPOINTS.some(e => this.suggestions()[e].loading));

  private requestIds: Partial<Record<Endpoint, symbol>> = {};
  private searchPromise: Record<string, any> = {};
  private searchStopRequest: Record<string, Subject<void>> =
    Object.fromEntries(ENDPOINTS.map(e => [e, new Subject<void>()]));

  private limit = 2;

  private dissmissed = false;

  /**
   * Cada pulsación lanzaba cuatro peticiones (categories, deals, stores, users).
   * Cancelaba las anteriores, pero abría y abortaba cuatro conexiones por carácter.
   * Con 250 ms de espera, escribir "zapatillas" pasa de ~40 peticiones a 4.
   */
  private searchInput$ = new Subject<string>();

  // `#searchText` es un <input> nativo, así que esto es un ElementRef; se tipaba como
  // IonSearchbar por herencia de cuando lo era, y por eso focusInput contempla los dos.
  private readonly searchbr = viewChild<any>('searchText');
  private readonly searchbar_container = viewChild<ElementRef>('searchbar_container');

  constructor() {
    this.route.queryParams
      .pipe(takeUntilDestroyed())
      .subscribe(query => { if (query?.search) this.searchTerm.set(query.search); });
  }

  ngOnInit() {
    this.destroyRef.onDestroy(() => {
      for (const endpoint of ENDPOINTS) this.cancelRequest(endpoint);
    });
    this.searchInput$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(term => { if (term !== '') this.runSearch(term); });
  }

  /**
   * Avisa de que hay que cerrar. Lo escucha quien lo pinta EN LÍNEA (la barra, en móvil),
   * donde no hay overlay que cerrar. El bucle de abajo ya era tolerante a que no hubiera
   * ninguno, así que los dos modos conviven sin preguntar.
   */
  readonly cerrar = output<void>();

  async dismiss() {
    this.searchInput$.next('');
    for (const endpoint of ENDPOINTS) this.cancelRequest(endpoint);
    this.searchTerm.set('');

    if (!this.mobile()) {
      this.searchOpened.set(false);
      return;
    }

    for (const cerrar of [this.popover, this.modal]) {
      try { await cerrar.dismiss(); } catch { /* no había ninguno abierto */ }
    }
    this.cerrar.emit();
  }

  search(event: any) {
    const searchText = String(event ?? '').trim();

    for (const endpoint of ENDPOINTS) this.cancelRequest(endpoint);
    // Vaciar el campo se aplica al momento: es una limpieza local, no una petición.
    if (searchText === '') {
      this.searchInput$.next('');
      this.runSearch('');
      return;
    }

    // Una sola letra casa con casi todo el catálogo: es la consulta más cara y la
    // que menos sirve. A partir de dos ya es una búsqueda real ("tv", "pc").
    this.searchInput$.next(searchText.length < 2 ? '' : searchText);
    if (searchText.length < 2) this.runSearch('');
  }

  clearSearch() {
    this.dissmissed = true;
    const searchbr: any = this.searchbr();
    if (searchbr) searchbr.value = '';
    this.searchTerm.set('');

    this.suggestions.set(SUGERENCIAS_VACIAS());
    this.searchInput$.next('');
    this.runSearch('');
    this.loading.set(false);

    this.dismiss();
  }

  /**
   * Punto de entrada único del submit del buscador: lo comparten el Enter y el clic
   * en la lupa. El error original nacía aquí: con el campo vacío `searchTerm` es
   * `undefined`, y `searchTag(undefined)` ejecutaba `undefined.trim()` → TypeError.
   *
   * Ahora saneamos el término ANTES de nada. Si tras el trim queda vacío o con menos
   * de dos caracteres (una sola letra casa con casi todo y no es una búsqueda real),
   * cancelamos el evento, mantenemos el foco en el input y avisamos en bajito. Sin
   * navegar ni pegar a la API.
   */
  submitSearch(event?: Event) {
    const searchText = String(this.searchTerm() ?? '').trim();

    if (searchText.length < 2) {
      event?.preventDefault();
      this.focusInput();
      this.ui.showToast('Escribe qué quieres buscar', 'warning', 2500);
      return;
    }

    this.searchTag(searchText);
  }

  searchTag(event: any) {
    // Doble red de seguridad: aunque submitSearch ya sanea, searchTag nunca debe
    // reventar si le llega un valor suelto (undefined, espacios, una letra).
    const searchText = String(event ?? '').trim();

    if (searchText.length < 2) return;

    try {
      this.recientes.add({ name: searchText });
      this.analytics.trackSearch(searchText);
      this.dismiss();
      this.nav.navigateForward('/results/', { queryParams: { search: searchText } });
    } catch (error) {
      console.error(error);
    }
  }

  click($event: Event) {
    // El micrófono está dentro de la caja pero abre su propia ventana: no cuenta como abrir la lista.
    const enMicrofono = !!($event.target as Element | null)?.closest?.('.voz-slot');
    const dentro = !this.dissmissed && !enMicrofono && !!this.searchbar_container()?.nativeElement.contains($event.target);

    this.searchOpened.set(dentro);
    if (!dentro) {
      this.searchInput$.next('');
      this.loading.set(false);
      for (const endpoint of ENDPOINTS) this.cancelRequest(endpoint);
    }

    this.dissmissed = false;
  }

  private runSearch(searchText: string) {
    for (const endpoint of ENDPOINTS) this.newSearch(endpoint, searchText);
  }

  private cancelRequest(endpoint: Endpoint) {
    this.requestIds[endpoint] = Symbol();
    this.actualizar(endpoint, { loading: false });
    this.rest.cancelGetApi(this.searchPromise[endpoint], this.searchStopRequest[endpoint]);
  }

  /** Cambia SOLO una de las cuatro secciones, dejando el objeto nuevo. */
  private actualizar(endpoint: Endpoint, cambios: Partial<Sugerencia>) {
    this.suggestions.update(actual => ({ ...actual, [endpoint]: { ...actual[endpoint], ...cambios } }));
  }

  private newSearch(endpoint: Endpoint, searchText: string) {
    try {
      this.cancelRequest(endpoint);
      const requestId = this.requestIds[endpoint];

      if (searchText === '') {
        this.actualizar(endpoint, { results: [], loading: false });
        return;
      }

      this.actualizar(endpoint, { loading: true });

      this.searchPromise[endpoint] = this.rest.getApi({
        path: `search/${endpoint}`,
        params: { limit: this.limit, keywords: searchText },
        stopRequest: this.searchStopRequest[endpoint]
      }, {
        next: (response: any) => {
          if (this.requestIds[endpoint] !== requestId) return;
          if (response && response.ok) {
            const results = response[endpoint].results.map((d: any) => {
              const resaltado = (texto: string) => texto.replace(new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'), '<b>$1</b>');

              if (endpoint === 'users') d.usernameHTML = resaltado(d.username);
              else d.nameHTML = resaltado(d.name);

              if (d.image_url) d.image_url = d.image_url.replace('/60/', '/48/');

              return d;
            });

            this.actualizar(endpoint, { results, total: response[endpoint].total, loading: false });
          } else {
            this.actualizar(endpoint, { loading: false });
          }
        },
        complete: () => {
          if (this.requestIds[endpoint] !== requestId) return;
          this.searchPromise[endpoint] = null;
          this.actualizar(endpoint, { loading: false });
        },
        error: (err: any) => {
          if (this.requestIds[endpoint] !== requestId) return;
          console.error(err);
          this.actualizar(endpoint, { loading: false });
        }
      });
    } catch (error) {
      console.error(error);
      this.ui.showToast('Error al hacer la búsqueda', 'error');
    }
  }

  /** Devuelve el foco al buscador sin acoplarnos a si es un <input> o un IonSearchbar. */
  private focusInput() {
    const input: any = this.searchbr();
    try {
      if (input?.setFocus) input.setFocus();
      else input?.nativeElement?.focus?.();
    } catch { /* el input aún no existe */ }
  }
}
